import { observable, makeObservable, action, computed } from 'mobx';
import { Moment } from 'moment';
import { TDictObject } from 'src/api/directories/types';
import { mapApproachStageSection } from 'src/api/new-well/serializers/approaches-serializers';
import {
  TApprachStageSectionsList,
  TApproachStageSectionRaw,
  TRequiredIf,
  TSerializedSection,
} from 'src/api/new-well/types';
import { EventBus } from 'src/shared/utils/event-bus';
import { Directories } from 'src/store/directories/directories.store';

import { TOption } from '../../types';
import { IFormEvents } from '../form.entity';

import { ApproachStageSection } from './approach-stage-section.entity';
import { ApproachStage } from './approach-stage.entity';
import { Approach } from './approach.entity';

type TApproachStageSectionsListData = {
  directories: Directories;
  sectionTemplate: TApprachStageSectionsList;
  approach: Approach;
  stage: ApproachStage;
  savedSections?: TSerializedSection[] | undefined;
};

function processSectionOptions(data: TDictObject[] | null): TOption[] {
  if (!data) return [];
  return data
    .filter((section) => section.status === 'ACTIVE')
    .map((sectionType) => ({
      label: sectionType.data.name.toString(),
      value: sectionType.id,
    }));
}

export class ApproachStageSectionsList {
  readonly eventBus: EventBus<IFormEvents>;
  readonly directories: Directories;
  readonly requiredIf: TRequiredIf[];
  readonly sectionReference: TApproachStageSectionRaw;
  readonly attrName: string;
  readonly sectionTypes: TDictObject[];
  readonly rigOperation: Approach;
  readonly stage: ApproachStage;

  @observable sectionOptions: TOption[];
  @observable sections: ApproachStageSection[];
  @observable required: boolean;

  constructor(data: TApproachStageSectionsListData, eventBus: EventBus<IFormEvents>) {
    this.directories = data.directories;
    this.eventBus = eventBus;
    this.rigOperation = data.approach;
    this.stage = data.stage;
    this.attrName = data.sectionTemplate.attrName;
    this.required = !!data.sectionTemplate.required;
    this.requiredIf = data.sectionTemplate.requiredIf;
    this.sectionReference = data.sectionTemplate.section;
    this.sectionOptions = processSectionOptions(
      this.directories.getObject(this.sectionReference.sectionTypeId.refObjectType),
    );
    this.sectionTypes = this.directories.getObject(this.sectionReference.sectionTypeId.refObjectType) ?? [];

    const sections = this.getSavedSections(data.savedSections) ?? [];

    this.sections = sections;
    // this.eventBus.publish('initializedSectionsList', { rigOperation: this.rigOperation, stage: this.stage, sections });

    makeObservable(this);
  }

  private getSavedSections = (rawSavedSections?: TSerializedSection[]): ApproachStageSection[] | undefined => {
    if (!rawSavedSections || !this.sectionReference) return;
    const reference = this.sectionReference;
    const sectionTypesDirectoryValues = this.directories.getObject(this.sectionReference.sectionTypeId.refObjectType);
    const savedSections: ApproachStageSection[] = [];

    rawSavedSections.forEach((savedSection) => {
      const sectionDirValue = sectionTypesDirectoryValues?.find(
        (dirValue) => dirValue.id === savedSection.sectionTypeId,
      );
      if (sectionDirValue) {
        const section = mapApproachStageSection(
          { sectionType: sectionDirValue, reference, directories: this.directories, parentControl: this.stage },
          savedSection,
        );

        savedSections.push(section);
      }
    });

    return savedSections;
  };

  @computed
  get startDate(): Moment | null {
    return this.sections.at(0)?.dateField?.startDate || null;
  }

  @computed
  get endDate(): Moment | null {
    return this.sections.at(-1)?.dateField?.endDate || null;
  }

  @action.bound
  setSections(sections: ApproachStageSection[]): void {
    this.sections = sections;

    // this.eventBus.publish('updatedSectionsList', { rigOperation: this.rigOperation, stage: this.stage, sections });
  }

  @action.bound
  setSectionsAndSwapDates(sections: ApproachStageSection[], newIndex: number, oldIndex: number) {
    const firstSection = sections.at(newIndex);
    const secondSection = sections.at(oldIndex);

    const firstSectionStartDate = firstSection?.dateField?.startDate;
    const secondSectionStartDate = secondSection?.dateField?.startDate;

    if (firstSection?.dateField && secondSectionStartDate) {
      firstSection.dateField.setStartDate(secondSectionStartDate);
    }

    if (secondSection?.dateField && firstSectionStartDate) {
      secondSection.dateField.setStartDate(firstSectionStartDate);
    }

    this.setSections(sections);
  }

  @action.bound
  changeSectionType(section: ApproachStageSection, sectionTypeId: string | number): void {
    if (!this.sectionReference) return;
    const newSections = [...this.sections];
    const fieldValues: { name: string; value: unknown }[] = [];

    section.fieldsList.forEach((field) => {
      if (field.value !== undefined) {
        fieldValues.push({
          name: field.fieldId,
          value: field.value,
        });
      }
    });

    const sectionType = this.sectionTypes.find((type) => type.id === sectionTypeId);
    if (!sectionType) return;

    const newSection = mapApproachStageSection({
      sectionType,
      fieldValues,
      directories: this.directories,
      reference: this.sectionReference,
      parentControl: this.stage,
    });

    const oldSectionIndex = this.sections.indexOf(section);
    newSections.splice(oldSectionIndex, 1, newSection);
    this.sections = newSections;

    // this.eventBus.publish('updatedSectionsList', {
    //   rigOperation: this.rigOperation,
    //   stage: this.stage,
    //   sections: newSections,
    // });
  }

  @action.bound
  addNewSection(sectionTypeId: string | number): void {
    if (!this.sectionReference) return;
    const sectionType = this.sectionTypes.find((type) => type.id === sectionTypeId);
    if (!sectionType) return;

    const section = mapApproachStageSection({
      sectionType,
      directories: this.directories,
      reference: this.sectionReference,
      parentControl: this.stage,
    });

    this.sections.push(section);

    // this.eventBus.publish('newSection', { rigOperation: this.rigOperation, stage: this.stage, section });
  }

  @action.bound
  deleteSection(id: number): void {
    const targetSection = this.sections.find((section) => section.id === id);

    if (!targetSection) {
      console.error('cannot find targetSection with given id', id);
      return;
    }

    targetSection?.onDeleteCallbacks.forEach((clb) => clb());

    this.sections = this.sections.filter((item) => item.id !== id);
    // this.eventBus.publish('deleteSection', {
    //   rigOperation: this.rigOperation,
    //   stage: this.stage,
    //   section: targetSection,
    // });
  }

  @action.bound
  validate() {
    this.sections.forEach((section) => section.validate());
  }

  @action.bound
  setIsVisuallyDisabled(is: boolean) {
    this.sections.forEach((section) => section.setIsVisuallyDisabled(is));
  }

  @action.bound
  setIsRequired(is: boolean) {
    this.required = is;
  }
}
